/**
 * FITUR OWNER PRO: Reset, Info, Simulasi
 * Komponen tambahan untuk Dashboard Owner
 */

import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  RotateCcw,
  Info,
  Database,
  Trash2,
  Play,
  AlertTriangle,
  X,
  CheckCircle,
  Users,
  Receipt,
  Wallet,
  Building2,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Eye
} from 'lucide-react';

const OwnerProTools = ({
  user,
  api,
  loadData,
  loading
}) => {
  const [showModal, setShowModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editSaldoEnabled, setEditSaldoEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Laporan Permissions Settings
  const [laporanPermissions, setLaporanPermissions] = useState({
    allow_edit_laporan: false,
    allow_delete_laporan: false
  });

  // Load edit saldo setting
  useEffect(() => {
    loadEditSaldoSetting();
    loadLaporanPermissions();
  }, []);

  const loadLaporanPermissions = async () => {
    try {
      const data = await api.get('/api/settings/laporan-permissions');
      setLaporanPermissions(data);
    } catch (err) {
      console.error('Gagal memuat laporan permissions:', err);
    }
  };

  const loadEditSaldoSetting = async () => {
    setLoadingSettings(true);
    try {
      const data = await api.get('/api/settings/edit-saldo');
      setEditSaldoEnabled(data.enabled || false);
    } catch (err) {
      console.error('Gagal memuat setting edit saldo:', err);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleToggleEditSaldo = async (enabled) => {
    if (!confirm(`${enabled ? 'Aktifkan' : 'Nonaktifkan'} fitur edit saldo untuk Penjaga Loket?\n\n${enabled ? 'Penjaga Loket akan dapat mengedit saldo awal langsung di menu pelaporan shift.' : 'Penjaga Loket TIDAK akan dapat mengedit saldo awal di menu pelaporan shift.'}\n\nCatatan: Role lain (Manager, Kasir, Owner) tetap selalu boleh edit saldo awal.`)) {
      return;
    }

    try {
      await api.put(`/api/settings/edit-saldo?enabled=${enabled}`);
      setEditSaldoEnabled(enabled);
      setSuccessMsg(`Fitur edit saldo berhasil ${enabled ? 'diaktifkan' : 'dinonaktifkan'}`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal mengubah setting edit saldo');
    }
  };

  const handleToggleLaporanPermission = async (key, value) => {
    const featureName = key === 'allow_edit_laporan' ? 'Edit Laporan' : 'Hapus Laporan';
    if (!confirm(`${value ? 'Aktifkan' : 'Nonaktifkan'} fitur ${featureName}?\n\n${value ? `Penjaga Loket akan dapat ${featureName.toLowerCase()} di menu riwayat laporan.` : `Penjaga Loket TIDAK akan dapat ${featureName.toLowerCase()} di menu riwayat laporan.`}`)) {
      return;
    }

    // Create new state with the toggled value
    const newState = { ...laporanPermissions, [key]: value };

    try {
      // Kirim update ke backend dengan nilai BARU
      await api.put(`/api/settings/laporan-permissions?allow_edit=${newState.allow_edit_laporan}&allow_delete=${newState.allow_delete_laporan}`);

      // Reload settings dari backend untuk memastikan sinkron
      await loadLaporanPermissions();

      setSuccessMsg(`Fitur ${featureName} berhasil ${value ? 'diaktifkan' : 'dinonaktifkan'}`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal mengubah setting');
      console.error('Error updating laporan permissions:', err);
    }
  };

  // Reset semua transaksi
  const handleResetTransaksi = async () => {
    if (!confirm('Yakin ingin mereset SEMUA transaksi PPOB?\n\nData yang akan dihapus:\n- Semua transaksi PPOB (TUNAI & NON-TUNAI)\n- Semua laporan shift loket (agar saldo awal kembali ke 0)\n\nData tidak dapat dikembalikan!')) {
      return;
    }

    setProcessing(true);
    try {
      const result = await api.post('/admin/reset-transaksi');
      setSuccessMsg(`Semua transaksi PPOB berhasil direset! (${result.deleted_laporan || 0} laporan shift dihapus)`);
      setTimeout(() => {
        setSuccessMsg('');
        loadData();
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal reset transaksi');
    } finally {
      setProcessing(false);
    }
  };

  // Reset deposit provider
  const handleResetDeposit = async () => {
    if (!confirm('Yakin ingin mereset semua deposit provider?\n\nData yang akan dihapus:\n- Saldo deposit akan dikembalikan ke 0\n- History deposit akan dihapus\n- Semua laporan shift loket (agar saldo awal kembali ke 0)\n\nData tidak dapat dikembalikan!')) {
      return;
    }

    setProcessing(true);
    try {
      const result = await api.post('/admin/reset-deposit');
      setSuccessMsg(`Semua deposit provider berhasil direset! (${result.deleted_laporan || 0} laporan shift dihapus)`);
      setTimeout(() => {
        setSuccessMsg('');
        loadData();
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal reset deposit');
    } finally {
      setProcessing(false);
    }
  };

  // Reset laporan (kasir & manager)
  const handleResetLaporan = async () => {
    if (!confirm('Yakin ingin mereset semua laporan?\n\nData yang akan dihapus:\n- Laporan shift loket (termasuk saldo awal)\n- Laporan kasir\n- Laporan manager\n- Pengeluaran kasir\n\nData tidak dapat dikembalikan!')) {
      return;
    }

    setProcessing(true);
    try {
      await api.post('/admin/reset-laporan');
      setSuccessMsg('Semua laporan berhasil direset! (termasuk semua saldo awal)');
      setTimeout(() => {
        setSuccessMsg('');
        loadData();
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal reset laporan');
    } finally {
      setProcessing(false);
    }
  };

  // Load info data
  const loadInfoData = async () => {
    setProcessing(true);
    try {
      const result = await api.get('/admin/info-data');
      setModalData(result);
      setShowModal('info');
    } catch (err) {
      setErrorMsg(err.message || 'Gagal memuat info data');
    } finally {
      setProcessing(false);
    }
  };

  // Load hapus data options
  const loadHapusOptions = async () => {
    setProcessing(true);
    try {
      const result = await api.get('/admin/hapus-options');
      setModalData(result);
      setShowModal('hapus');
    } catch (err) {
      setErrorMsg(err.message || 'Gagal memuat opsi hapus');
    } finally {
      setProcessing(false);
    }
  };

  // Hapus data spesifik
  const handleHapusData = async (kategori, subKategori = null) => {
    const kategoriNama = {
      'transaksi': 'Transaksi PPOB',
      'deposit': 'Deposit Provider',
      'laporan': 'Laporan (Loket/Kasir/Manager)',
      'pengeluaran': 'Pengeluaran Kasir'
    }[kategori];

    const confirmMsg = subKategori
      ? `Yakin ingin menghapus semua ${subKategori} dari ${kategoriNama}?`
      : `Yakin ingin menghapus SEMUA data ${kategoriNama}?`;

    if (!confirm(confirmMsg + '\n\nData tidak dapat dikembalikan!')) {
      return;
    }

    setProcessing(true);
    try {
      await api.post('/admin/hapus-data', { kategori, sub_kategori: subKategori });
      setSuccessMsg(`Data ${subKategori || kategoriNama} berhasil dihapus!`);
      setShowModal(null);
      setTimeout(() => {
        setSuccessMsg('');
        loadData();
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal menghapus data');
    } finally {
      setProcessing(false);
    }
  };

  // Simulasi data
  const handleSimulasiData = async () => {
    if (!confirm('Generate data simulasi untuk testing?\n\nIni akan membuat:\n- Transaksi simulasi\n- Laporan simulasi\n- Data random untuk testing\n\nGunakan hanya untuk development/testing!')) {
      return;
    }

    setProcessing(true);
    try {
      const result = await api.post('/admin/simulasi-data');
      setSuccessMsg(`Simulasi berhasil dibuat! ${result.message || ''}`);
      setTimeout(() => {
        setSuccessMsg('');
        loadData();
      }, 3000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal membuat simulasi');
    } finally {
      setProcessing(false);
    }
  };

  // Load user credentials
  const loadUserCredentials = async () => {
    setProcessing(true);
    try {
      const users = await api.get('/api/users-with-passwords');
      setModalData({ users: users });
      setShowModal('currentUser-credentials');
    } catch (err) {
      setErrorMsg(err.message || 'Gagal memuat data user');
    } finally {
      setProcessing(false);
    }
  };

  // Verify password for all users
  const verifyPasswords = async () => {
    const testPassword = prompt('Masukkan password untuk verifikasi:');
    if (!testPassword) return;
    setProcessing(true);
    try {
      const results = await api.post('/api/users-verify-passwords', { test_password: testPassword });
      // Update modalData with verification results
      const currentUsers = modalData?.users || [];
      const updatedUsers = currentUsers.map(user => {
        const verification = results.find(r => r.id === user.id);
        return {
          ...user,
          password_valid: verification ? verification.password_valid : false
        };
      });
      setModalData({ users: updatedUsers });
      setSuccessMsg('Password berhasil diverifikasi!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err.message || 'Gagal memverifikasi password');
    } finally {
      setProcessing(false);
    }
  };

  // Reset user password
  const handleResetPassword = async (userId, username) => {
    const newPassword = prompt(`Masukkan password baru untuk user "${username}":`);
    if (!newPassword || newPassword.length < 6) {
      setErrorMsg('Password minimal 6 karakter');
      return;
    }

    if (!confirm(`Yakin ingin mengubah password user "${username}"?`)) {
      return;
    }

    setProcessing(true);
    try {
      await api.put(`/api/users/${userId}/password?new_password=${encodeURIComponent(newPassword)}`);
      setSuccessMsg(`Password user "${username}" berhasil direset!`);
      setTimeout(() => setSuccessMsg(''), 3000);
      // Reload user list
      loadUserCredentials();
    } catch (err) {
      setErrorMsg(err.message || 'Gagal mereset password');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      {/* Success/Error Messages */}
      {successMsg && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-4">
          <div className="flex items-center">
            <CheckCircle className="text-green-500 mr-2" size={20} />
            <p className="text-green-700">{successMsg}</p>
          </div>
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-4">
          <div className="flex items-center">
            <AlertTriangle className="text-red-500 mr-2" size={20} />
            <p className="text-red-700">{errorMsg}</p>
            <button
              onClick={() => setErrorMsg('')}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Settings Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="text-purple-600" size={24} />
          <h2 className="text-xl font-bold text-gray-900">Pengaturan Sistem</h2>
        </div>

        <div className="space-y-4">
          {/* Edit Saldo Setting */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert className="text-orange-500" size={18} />
                  <h3 className="font-semibold text-gray-900">Edit Saldo Penjaga Loket</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Izinkan Penjaga Loket mengedit saldo awal bank (BRIS/Mandiri) di menu pelaporan shift.
                  {editSaldoEnabled ? (
                    <span className="ml-2 text-green-600 font-medium">[AKTIF]</span>
                  ) : (
                    <span className="ml-2 text-red-600 font-medium">[NON-AKTIF]</span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Role lain (Manager, Kasir, Owner) selalu boleh edit saldo awal.
                </p>
                {editSaldoEnabled && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                    <p className="text-sm text-yellow-800">
                      <strong>Peringatan:</strong> Penjaga Loket dapat mengedit saldo awal tanpa approve.
                      Matikan untuk kontrol lebih ketat.
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={() => handleToggleEditSaldo(!editSaldoEnabled)}
                disabled={loadingSettings}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editSaldoEnabled ? 'bg-purple-600' : 'bg-gray-300'
                } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editSaldoEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-gray-500">Status: {editSaldoEnabled ? 'Aktif' : 'Non-aktif'}</span>
              {loadingSettings && <span className="text-gray-400">Memuat...</span>}
            </div>
          </div>

          {/* Laporan Permissions Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="text-blue-500" size={18} />
              <h3 className="font-semibold text-gray-900">Izin Akses Laporan Shift</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Atur izin untuk Penjaga Loket mengedit atau menghapus laporan shift di menu riwayat.
            </p>

            <div className="space-y-3">
              {/* Edit Laporan */}
              <div className="flex items-start justify-between py-2 border-b">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">Edit Laporan</span>
                    {laporanPermissions.allow_edit_laporan ? (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">AKTIF</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">NON-AKTIF</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Izinkan Penjaga Loket mengedit laporan shift yang sudah dibuat
                  </p>
                </div>
                <button
                  onClick={() => handleToggleLaporanPermission('allow_edit_laporan', !laporanPermissions.allow_edit_laporan)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    laporanPermissions.allow_edit_laporan ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      laporanPermissions.allow_edit_laporan ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Delete Laporan */}
              <div className="flex items-start justify-between py-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">Hapus Laporan</span>
                    {laporanPermissions.allow_delete_laporan ? (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">AKTIF</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">NON-AKTIF</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Izinkan Penjaga Loket menghapus laporan shift (permanen)
                  </p>
                </div>
                <button
                  onClick={() => handleToggleLaporanPermission('allow_delete_laporan', !laporanPermissions.allow_delete_laporan)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    laporanPermissions.allow_delete_laporan ? 'bg-red-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      laporanPermissions.allow_delete_laporan ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Refresh */}
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>

        {/* Reset Dropdown */}
        <div className="relative inline-block">
          <button
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            onClick={() => setShowModal(showModal === 'reset' ? null : 'reset')}
          >
            <RotateCcw size={16} />
            Reset Data
          </button>

          {/* Reset Dropdown Menu */}
          {showModal === 'reset' && (
            <div className="absolute top-full mt-2 left-0 bg-white rounded-lg shadow-lg border z-50 min-w-56">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => { setShowModal(null); handleResetTransaksi(); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-2 text-sm"
                  disabled={processing}
                >
                  <Receipt size={16} />
                  Reset Transaksi PPOB
                </button>
                <button
                  onClick={() => { setShowModal(null); handleResetDeposit(); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-2 text-sm"
                  disabled={processing}
                >
                  <Wallet size={16} />
                  Reset Deposit
                </button>
                <button
                  onClick={() => { setShowModal(null); handleResetLaporan(); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-2 text-sm"
                  disabled={processing}
                >
                  <Building2 size={16} />
                  Reset Laporan
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info Data */}
        <button
          onClick={loadInfoData}
          disabled={processing}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Info size={16} />
          Info Data
        </button>

        {/* Hapus Data */}
        <button
          onClick={loadHapusOptions}
          disabled={processing}
          className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
        >
          <Trash2 size={16} />
          Hapus Data
        </button>

        {/* Simulasi */}
        <button
          onClick={handleSimulasiData}
          disabled={processing}
          className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
        >
          <Play size={16} />
          Simulasi Data
        </button>

        {/* Kelola User */}
        <button
          onClick={loadUserCredentials}
          disabled={processing}
          className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
        >
          <Users size={16} />
          Kelola User
        </button>
      </div>

      {/* Info Modal */}
      {showModal === 'info' && modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Database size={20} />
                Info Data Sistem
              </h3>
              <button
                onClick={() => { setShowModal(null); setModalData(null); }}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Transaksi PPOB */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Receipt className="text-green-600" size={18} />
                  Transaksi PPOB
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-gray-500">Total Transaksi</p>
                    <p className="font-bold text-lg">{modalData.transaksi?.total || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-gray-500">Total Omzet</p>
                    <p className="font-bold text-lg text-green-600">
                      {new Intl.NumberFormat('id-ID').format(modalData.transaksi?.total_omzet || 0)}
                    </p>
                  </div>
                </div>
                {modalData.transaksi?.by_kategori && (
                  <div className="mt-3 space-y-2">
                    <p className="font-medium text-gray-700">Per Kategori:</p>
                    {Object.entries(modalData.transaksi.by_kategori).map(([kat, data]) => (
                      <div key={kat} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded">
                        <span className="text-gray-700">{kat}</span>
                        <span className="font-semibold">{data.jumlah} transaksi</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Deposit */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Wallet className="text-purple-600" size={18} />
                  Deposit Provider
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-gray-500">Jumlah Provider</p>
                    <p className="font-bold text-lg">{modalData.deposit?.jumlah_provider || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-gray-500">Total Saldo</p>
                    <p className="font-bold text-lg text-purple-600">
                      {new Intl.NumberFormat('id-ID').format(modalData.deposit?.total_saldo || 0)}
                    </p>
                  </div>
                </div>
                {modalData.deposit?.by_provider && (
                  <div className="space-y-2">
                    {modalData.deposit.by_provider.map((dep, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-purple-50 px-3 py-2 rounded">
                        <span className="text-gray-700">{dep.provider_nama}</span>
                        <span className="font-semibold text-purple-700">
                          {new Intl.NumberFormat('id-ID').format(dep.saldo)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Laporan */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="text-orange-600" size={18} />
                  Laporan
                </h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-orange-50 p-3 rounded text-center">
                    <p className="text-gray-500">Loket</p>
                    <p className="font-bold text-lg text-orange-700">{modalData.laporan?.loket || 0}</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded text-center">
                    <p className="text-gray-500">Kasir</p>
                    <p className="font-bold text-lg text-orange-700">{modalData.laporan?.kasir || 0}</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded text-center">
                    <p className="text-gray-500">Manager</p>
                    <p className="font-bold text-lg text-orange-700">{modalData.laporan?.manager || 0}</p>
                  </div>
                </div>
              </div>

              {/* Pengeluaran */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="text-red-600" size={18} />
                  Pengeluaran Kasir
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-red-50 p-3 rounded">
                    <p className="text-gray-500">Total Pengeluaran</p>
                    <p className="font-bold text-lg text-red-700">
                      {new Intl.NumberFormat('id-ID').format(modalData.pengeluaran?.total || 0)}
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded">
                    <p className="text-gray-500">Jumlah Record</p>
                    <p className="font-bold text-lg">{modalData.pengeluaran?.jumlah || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hapus Data Modal */}
      {showModal === 'hapus' && modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full">
            <div className="bg-orange-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Trash2 size={20} />
                Hapus Data
              </h3>
              <button
                onClick={() => { setShowModal(null); setModalData(null); }}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Pilih kategori data yang ingin dihapus. Tindakan ini tidak dapat dibatalkan!
              </p>

              <div className="space-y-4">
                {/* Transaksi */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Transaksi PPOB</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleHapusData('transaksi', 'SEMUA')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      disabled={processing}
                    >
                      Hapus Semua
                    </button>
                    <button
                      onClick={() => handleHapusData('transaksi', 'HARI_INI')}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                      disabled={processing}
                    >
                      Hari Ini
                    </button>
                  </div>
                  {modalData.transaksi?.by_provider?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Per Provider:</p>
                      {modalData.transaksi.by_provider.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleHapusData('transaksi', item.provider_id)}
                          className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-red-50 rounded text-sm"
                          disabled={processing}
                        >
                          {item.provider_nama} ({item.jumlah} trx)
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Deposit */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Deposit Provider</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleHapusData('deposit', 'SEMUA')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      disabled={processing}
                    >
                      Hapus Semua
                    </button>
                    <button
                      onClick={() => handleHapusData('deposit', 'RESET_SALDO')}
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"
                      disabled={processing}
                    >
                      Reset Saldo (0)
                    </button>
                  </div>
                  {modalData.deposit?.by_provider?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Per Provider:</p>
                      {modalData.deposit.by_provider.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleHapusData('deposit', item.provider_id)}
                          className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-purple-50 rounded text-sm"
                          disabled={processing}
                        >
                          {item.provider_nama} (Saldo: {new Intl.NumberFormat('id-ID').format(item.saldo)})
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Laporan */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Laporan</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleHapusData('laporan', 'SEMUA')}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                      disabled={processing}
                    >
                      Hapus Semua
                    </button>
                    <button
                      onClick={() => handleHapusData('laporan', 'HARI_INI')}
                      className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm"
                      disabled={processing}
                    >
                      Hari Ini
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    <button
                      onClick={() => handleHapusData('laporan', 'LOKET')}
                      className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm"
                      disabled={processing}
                    >
                      Loket ({modalData.laporan?.jumlah_loket || 0})
                    </button>
                    <button
                      onClick={() => handleHapusData('laporan', 'KASIR')}
                      className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm"
                      disabled={processing}
                    >
                      Kasir ({modalData.laporan?.jumlah_kasir || 0})
                    </button>
                    <button
                      onClick={() => handleHapusData('laporan', 'MANAGER')}
                      className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm"
                      disabled={processing}
                    >
                      Manager ({modalData.laporan?.jumlah_manager || 0})
                    </button>
                  </div>
                </div>

                {/* Pengeluaran */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Pengeluaran</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleHapusData('pengeluaran', 'SEMUA')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      disabled={processing}
                    >
                      Hapus Semua
                    </button>
                    <button
                      onClick={() => handleHapusData('pengeluaran', 'HARI_INI')}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                      disabled={processing}
                    >
                      Hari Ini
                    </button>
                  </div>
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-gray-700">Per Jenis:</p>
                    {modalData.pengeluaran?.by_jenis?.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleHapusData('pengeluaran', item.jenis)}
                        className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-red-50 rounded text-sm"
                        disabled={processing}
                      >
                        {item.jenis} ({item.jumlah} record)
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Credentials Modal */}
      {showModal === 'currentUser-credentials' && modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden">
            <div className="bg-teal-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Users size={20} />
                Kelola User & Password
              </h3>
              <button
                onClick={() => { setShowModal(null); setModalData(null); }}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[65vh]">
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Catatan:</strong> Password disimpan dalam format terenkripsi (bcrypt). Klik tombol "Reset Password" untuk mengubah password user.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Username</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Password Hash</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Nama Lengkap</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Role</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Loket</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">
                        <button
                          onClick={verifyPasswords}
                          disabled={processing}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          title="Klik untuk verifikasi password"
                        >
                          <Eye size={14} />
                          Status Password
                        </button>
                      </th>
                      <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700 border-b">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.users && modalData.users.map((user, idx) => (
                      <tr key={user.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-sm text-gray-900 border-b font-medium">
                          {user.username}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 border-b font-mono text-xs">
                          {user.password_hash ? (user.password_hash.substring(0, 20) + '...') : '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 border-b">
                          {user.nama_lengkap || '-'}
                        </td>
                        <td className="px-4 py-2 text-sm border-b">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.role === 'OWNER' ? 'bg-red-100 text-red-800' :
                            user.role === 'MANAGER' ? 'bg-orange-100 text-orange-800' :
                            user.role === 'SUPERKASIR' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'KASIR' ? 'bg-blue-100 text-blue-800' :
                            user.role === 'PENJAGA_LOKET' ? 'bg-green-100 text-green-800' :
                            user.role === 'IT' || user.role === 'IT_SUPPORT' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 border-b">
                          {user.loket_id || '-'}
                        </td>
                        <td className="px-4 py-2 text-sm border-b">
                          {user.is_active !== false ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle size={14} /> Aktif
                            </span>
                          ) : (
                            <span className="text-red-600 flex items-center gap-1">
                              <X size={14} /> Nonaktif
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm border-b">
                          {user.password_valid === true ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <ShieldCheck size={14} /> Aktif
                            </span>
                          ) : user.password_valid === false ? (
                            <span className="text-red-600 flex items-center gap-1">
                              <X size={14} /> Tidak Aktif
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Belum Dicek</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center border-b">
                          <button
                            onClick={() => handleResetPassword(user.id, user.username)}
                            disabled={processing}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 flex items-center gap-1 mx-auto"
                          >
                            <RotateCcw size={12} />
                            Reset Password
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded text-sm">
                <p className="font-medium text-gray-700 mb-2">Total User: {modalData.users?.length || 0}</p>
                <div className="flex flex-wrap gap-2">
                  {['OWNER', 'MANAGER', 'SUPERKASIR', 'KASIR', 'PENJAGA_LOKET', 'IT', 'IT_SUPPORT', 'TECHNICIAN'].map(role => {
                    const count = modalData.users?.filter(u => u.role === role).length || 0;
                    if (count > 0) {
                      return (
                        <span key={role} className="px-2 py-1 bg-white border rounded text-xs">
                          {role}: {count}
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OwnerProTools;
